import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authAPI } from '../services/api';
import toast from 'react-hot-toast';
import { ShieldCheck, ArrowRight, Loader, LogIn, RefreshCw, Clock } from 'lucide-react';

const Verify2FA = () => {
    const { verify2FA } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    
    const userId = location.state?.userId;
    const [code, setCode] = useState('');
    const [loading, setLoading] = useState(false);
    const [resending, setResending] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    
    // Timer state (5 minutes = 300 seconds)
    const [timeLeft, setTimeLeft] = useState(300);
    const [canResend, setCanResend] = useState(false);

    useEffect(() => {
        if (!userId) {
            navigate('/login');
        }
    }, [userId, navigate]);

    // Countdown logic
    useEffect(() => {
        if (timeLeft <= 0) {
            setCanResend(true);
            return;
        }

        const timer = setInterval(() => {
            setTimeLeft(prev => prev - 1);
        }, 1000);

        return () => clearInterval(timer);
    }, [timeLeft]);

    const formatTime = (seconds) => {
        const m = Math.floor(seconds / 60);
        const s = seconds % 60;
        return `${m}:${s < 10 ? '0' : ''}${s}`;
    };

    const handleSubmit = async (e) => {
        if (e) e.preventDefault();
        if (code.length < 5) return toast.error('Le code doit contenir 5 chiffres');

        setLoading(true);
        try {
            await verify2FA(userId, code);
            toast.success('Connexion confirmée');
            navigate('/');
        } catch (err) {
            toast.error(err.response?.data?.message || 'Code invalide');
        } finally {
            setLoading(false);
        }
    };

    const handleResend = async () => {
        if (!canResend && timeLeft > 0) return;
        
        setResending(true);
        try {
            await authAPI.resend2FA(userId);
            toast.success('Un nouveau code a été envoyé');
            setTimeLeft(300); // Reset timer
            setCanResend(false);
            setCode(''); // Clear input
        } catch (err) {
            toast.error('Erreur lors del envío del nuevo código');
        } finally {
            setResending(false);
        }
    };

    if (!userId) return null;

    return (
        <div className="verify-2fa-viewport" style={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#071325',
            fontFamily: "'Inter', sans-serif",
            padding: '24px',
            position: 'relative',
            overflow: 'hidden'
        }}>
            {/* Background atmosphere - kinetic depth */}
            <div style={{
                position: 'absolute', width: '800px', height: '800px',
                background: 'radial-gradient(circle, rgba(242, 202, 80, 0.04) 0%, rgba(7, 19, 37, 0) 70%)',
                top: '-200px', right: '-200px', zIndex: 0
            }} />
            <div style={{
                position: 'absolute', width: '600px', height: '600px',
                background: 'radial-gradient(circle, rgba(242, 202, 80, 0.02) 0%, rgba(7, 19, 37, 0) 70%)',
                bottom: '-200px', left: '-200px', zIndex: 0
            }} />

            <div className="verify-card" style={{
                width: '100%',
                maxWidth: '440px',
                background: 'rgba(20, 32, 50, 0.7)',
                backdropFilter: 'blur(24px)',
                borderRadius: '24px',
                padding: '48px 40px',
                zIndex: 1,
                boxShadow: '0 32px 64px rgba(0, 0, 0, 0.5)',
                textAlign: 'center',
                position: 'relative',
                border: '1px solid rgba(215, 227, 252, 0.05)'
            }}>
                {/* HUD Decoration corners */}
                <div style={{ position: 'absolute', top: '16px', left: '16px', width: '12px', height: '12px', borderTop: '2px solid #f2ca50', borderLeft: '2px solid #f2ca50', opacity: 0.3 }} />
                <div style={{ position: 'absolute', bottom: '16px', right: '16px', width: '12px', height: '12px', borderBottom: '2px solid #f2ca50', borderRight: '2px solid #f2ca50', opacity: 0.3 }} />

                {/* Header Section */}
                <div style={{ marginBottom: '40px' }}>
                    <div style={{
                        width: '88px', height: '88px',
                        background: 'rgba(242, 202, 80, 0.05)',
                        borderRadius: '24px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 28px',
                        position: 'relative'
                    }}>
                        <ShieldCheck size={48} style={{ color: '#f2ca50', filter: 'drop-shadow(0 0 12px rgba(242, 202, 80, 0.4))' }} />
                        <div style={{
                            position: 'absolute', inset: '-4px',
                            border: '1px solid rgba(242, 202, 80, 0.1)',
                            borderRadius: '28px'
                        }} />
                    </div>
                    
                    <h1 style={{
                        color: '#ffffff',
                        fontSize: '26px',
                        fontWeight: '900',
                        marginBottom: '14px',
                        letterSpacing: '-0.03em'
                    }}>Vérification Sécurisée</h1>
                    
                    <p style={{
                        color: 'rgba(215, 227, 252, 0.5)',
                        fontSize: '14px',
                        lineHeight: '1.7',
                        maxWidth: '300px',
                        margin: '0 auto'
                    }}>
                        Pour protéger votre accès, veuillez saisir le code de confirmation envoyé par email.
                    </p>
                </div>

                {/* Form Section */}
                <form onSubmit={handleSubmit}>
                    <div style={{ marginBottom: '40px', position: 'relative' }}>
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', marginBottom: '20px' }}>
                            <Clock size={14} color={timeLeft < 60 ? '#ef4444' : '#f2ca50'} />
                            <span style={{ 
                                fontSize: '12px', 
                                fontWeight: '700', 
                                color: timeLeft < 60 ? '#ef4444' : '#f2ca50',
                                letterSpacing: '1px'
                            }}>
                                EXPIRATION DANS {formatTime(timeLeft)}
                            </span>
                        </div>
                        
                        <div style={{ position: 'relative' }}>
                            <input
                                type="text"
                                maxLength="5"
                                value={code}
                                onFocus={() => setIsFocused(true)}
                                onBlur={() => setIsFocused(false)}
                                onChange={(e) => {
                                    const val = e.target.value.replace(/\D/g, '');
                                    setCode(val);
                                    if (val.length === 5) {
                                        // Auto submit if 5 chars (optional but premium feel)
                                        // Wait a bit for UX
                                        // setTimeout(() => handleSubmit(), 200); 
                                    }
                                }}
                                autoFocus
                                autoComplete="off"
                                style={{
                                    width: '100%',
                                    background: 'rgba(16, 28, 46, 0.9)',
                                    border: '1px solid rgba(242, 202, 80, 0.05)',
                                    borderRadius: '16px',
                                    padding: '24px 0',
                                    textAlign: 'center',
                                    fontSize: '36px',
                                    fontWeight: '900',
                                    color: '#f2ca50',
                                    letterSpacing: '16px',
                                    outline: 'none',
                                    transition: 'all 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
                                    boxShadow: isFocused 
                                        ? '0 0 0 2px rgba(242, 202, 80, 0.15), 0 10px 30px rgba(0,0,0,0.3)' 
                                        : 'none',
                                    fontFamily: "'JetBrains Mono', 'Roboto Mono', monospace"
                                }}
                                placeholder="00000"
                            />
                            {/* Kinetic Underline */}
                            <div style={{
                                position: 'absolute',
                                bottom: '0',
                                left: '50%',
                                width: isFocused ? '80%' : '0%',
                                height: '2px',
                                background: 'linear-gradient(90deg, transparent, #f2ca50, transparent)',
                                transform: 'translateX(-50%)',
                                transition: 'width 0.6s cubic-bezier(0.22, 1, 0.36, 1)',
                                filter: 'blur(1px)'
                            }} />
                        </div>
                    </div>

                    <button 
                        type="submit" 
                        disabled={loading || timeLeft <= 0}
                        style={{
                            width: '100%',
                            height: '60px',
                            background: timeLeft <= 0 ? 'rgba(255,255,255,0.05)' : 'linear-gradient(135deg, #f2ca50 0%, #d4af37 100%)',
                            color: timeLeft <= 0 ? 'rgba(255,255,255,0.2)' : '#241a00',
                            border: 'none',
                            borderRadius: '14px',
                            fontSize: '16px',
                            fontWeight: '900',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '14px',
                            cursor: timeLeft <= 0 ? 'not-allowed' : 'pointer',
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            opacity: loading ? 0.7 : 1,
                            boxShadow: timeLeft > 0 ? '0 12px 32px rgba(212, 175, 55, 0.3)' : 'none',
                            transform: isFocused && timeLeft > 0 ? 'translateY(-2px)' : 'none'
                        }}
                    >
                        {loading ? <Loader className="animate-spin" size={24} /> : (
                            <>
                                {timeLeft <= 0 ? 'CODE EXPIRÉ' : 'CONFIRMER L\'ACCÈS'}
                                <ArrowRight size={22} />
                            </>
                        )}
                    </button>
                </form>

                {/* Resend / Action Section */}
                <div style={{ marginTop: '32px' }}>
                    <button 
                        onClick={handleResend}
                        disabled={resending || !canResend}
                        style={{
                            background: 'transparent',
                            border: 'none',
                            color: canResend ? '#f2ca50' : 'rgba(215, 227, 252, 0.3)',
                            fontSize: '13px',
                            fontWeight: '700',
                            cursor: canResend ? 'pointer' : 'default',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                            margin: '0 auto 32px',
                            padding: '10px 20px',
                            borderRadius: '10px',
                            transition: 'all 0.2s ease',
                            pointerEvents: resending || !canResend ? 'none' : 'auto'
                        }}
                    >
                        {resending ? <RefreshCw className="animate-spin" size={16} /> : <RefreshCw size={16} />}
                        {canResend ? 'Renvoyer un nouveau code' : `Renvoyer disponible dans ${formatTime(timeLeft)}`}
                    </button>

                    <div style={{
                        background: 'rgba(215, 227, 252, 0.03)',
                        borderRadius: '12px',
                        padding: '16px',
                        marginBottom: '32px',
                        borderLeft: '3px solid #f2ca50'
                    }}>
                        <p style={{
                            fontSize: '11px',
                            color: 'rgba(215, 227, 252, 0.4)',
                            textAlign: 'left',
                            lineHeight: '1.6',
                            margin: 0
                        }}>
                            <strong>Sécurité :</strong> Si vous ne recevez pas l'email, vérifiez vos spams ou contactez l'administrateur du cabinet.
                        </p>
                    </div>
                    
                    <button 
                        onClick={() => navigate('/login')}
                        style={{
                            background: 'transparent',
                            border: 'none',
                            color: 'rgba(215, 227, 252, 0.6)',
                            fontSize: '14px',
                            fontWeight: '700',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            margin: '0 auto',
                            padding: '10px 20px',
                            borderRadius: '10px',
                            transition: 'all 0.2s ease',
                            letterSpacing: '0.02em'
                        }}
                        onMouseEnter={(e) => {
                            e.target.style.background = 'rgba(215, 227, 252, 0.05)';
                            e.target.style.color = '#f2ca50';
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.background = 'transparent';
                            e.target.style.color = 'rgba(215, 227, 252, 0.6)';
                        }}
                    >
                        <LogIn size={18} />
                        Annuler et retourner au login
                    </button>
                </div>
            </div>

            <style>{`
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
                .animate-spin {
                    animation: spin 1s linear infinite;
                }
                input::placeholder {
                    color: rgba(242, 202, 80, 0.05);
                }
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&family=JetBrains+Mono:wght@700;800&display=swap');
            `}</style>
        </div>
    );
};

export default Verify2FA;
